import profile_img1 from '../../assets/images/pages/laptop.jpg';
import { Link, useMatch, useNavigate } from 'react-router-dom'
import { GlobalContext, handleError } from '../../context/Provider';
import { Fragment, useContext, useEffect, useState } from 'react';
import henceforthApi from '../../utils/henceforthApi';
import { CURRENCY_SYMBOL } from '../../utils/henceforthApi';
import { numberWithCommas } from '../../utils/validations';
import profile_placeholder from '../../assets/images/pages/profile_placeholder.png'
import moment from 'moment';
import { toast } from 'react-toastify';
import OrderStatus from '../../components/order/OrderStatus';
import henceofrthEnums from '../../utils/henceofrthEnums';
import { INSUFFICIENT_PERMISSIONS } from '../../context/actionTypes';
import SignedImage from '../../components/SignedImage';
import { getInvoiceSignedUrl } from '../../utils/signedUrl';

// TypeScript interfaces
interface SellerProduct {
    order_product_id: string;
    product_id: string;
    product_name: string;
    product_description: string;
    product_images: string[];
    quantity: number;
    price: number;
    discount_percentage: number;
    discount_price: number;
    actual_product_price: number;
    order_status: string;
    payment_status: string;
    tracking_link: string | null;
    delivery_date: string;
    delivery_type: string;
    reviews: any[];
    created_at: number;
    updated_at: number;
}

interface SellerInfo {
    seller_id: string;
    seller_name: string;
    seller_image?: string;
    seller_phone?: number;
    seller_company?: string;
    seller_address?: string;
    seller_invoice?: {
        invoice_url: string;
        admin_invoice_url?: string;
    };
    total_price: number;
    seller_earnings: number;
    admin_commission: number;
    tax_amount: number;
    coupon_discount: number;
    delivery_price: number;
    seller_platform_fee?: number;
    products: SellerProduct[];
}

interface OrderDetailsNewState {
    order_id: string;
    parent_order_id: string;
    user_info: {
        _id: string;
        profile_pic: string | null;
        name: string;
        phone_no?: number;
        country_code?: string | number;
    };
    address: {
        name: string;
        country_code: string;
        phone_no: number;
        company: string;
        country: string;
        state: string;
        city: string;
        pin_code: string;
        apartment_number: string;
        full_address: string;
        address_type: string;
        lat: string;
        lng: string;
    };
    delivery_type: string;
    sellers: SellerInfo[];
    customer_invoice: {
        invoice_url: string;
        order_id: string;
        subtotal: number;
        coupon_discount: number;
        delivery_charge: number;
        tax_amount: number;
        total_price: number;
        total_earnings: number;
        admin_commission: number;
        admin_earning: number;
        admin_fees_percentage: number;
        platform_fee: number;
    };
    payment_info: {
        _id: string;
        payment_intent: string;
        transaction_id: string;
        receipt_id: string | null;
        total_amount: number;
        currency: string;
        payment_status: string;
        payment_type: string;
        payment_gateway: string;
        gateway_response: any;
        refund_id: string | null;
        refund_amount: number | null;
        failure_reason: string | null;
        created_at: string;
        updated_at: string;
    } | null;
    created_at: number;
    updated_at: number;
}

const OrderDetailsNew = () => {
    const match = useMatch(`ordersdetails/:id`);
    const { loading, setLoading, authState } = useContext(GlobalContext);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'order' | 'seller'>('order');
    const [selectedSellerIndex, setSelectedSellerIndex] = useState(0);
    const [state, setstate] = useState({} as OrderDetailsNewState);
    const [signedInvoiceUrl, setSignedInvoiceUrl] = useState<string>('');
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [loadingSellerInvoice, setLoadingSellerInvoice] = useState(false);

    const initialise = async () => {
        try {
            let apiRes = await henceforthApi.Order.orderDetails(match?.params.id);
            setstate(apiRes.data);
        } catch (error: any) {
            if (error?.response?.body?.error === INSUFFICIENT_PERMISSIONS) {
                window.history.back();
            }
        }
    };

    const handleDownloadInvoice = async () => {
        if (!state?.customer_invoice?.invoice_url) {
            toast.error('Invoice URL not available');
            return;
        }

        try {
            setLoadingInvoice(true);
            // Extract the file key from the full URL
            const invoiceUrl = state.customer_invoice.invoice_url;
            let fileKey = invoiceUrl;

            // If it's a full URL, extract just the key part
            if (invoiceUrl.includes('amazonaws.com')) {
                const urlParts = invoiceUrl.split('.amazonaws.com/');
                if (urlParts.length > 1) {
                    fileKey = urlParts[1];
                }
            }

            console.log('Original invoice URL:', invoiceUrl);
            console.log('Extracted file key:', fileKey);

            // Generate signed URL
            const signedUrl = await getInvoiceSignedUrl(fileKey, 3600);
            console.log('Generated signed URL:', signedUrl);

            setSignedInvoiceUrl(signedUrl);

            // Open in new tab
            window.open(signedUrl, '_blank');
        } catch (error) {
            console.error('Error generating invoice signed URL:', error);
            toast.error('Failed to generate invoice download link');
        } finally {
            setLoadingInvoice(false);
        }
    };

    const getPaymentDisplay = () => {
        const gateway = state?.payment_info?.payment_gateway?.toLowerCase();
        const type = state?.payment_info?.payment_type?.toLowerCase();

        if (gateway === 'stripe') {
            return { paymentBy: 'Card', paymentGateway: 'Stripe' };
        }
        if (type === 'yonna') {
            return { paymentBy: 'Yonna Wallet', paymentGateway: 'Yonna' };
        }
        return {
            paymentBy: state?.payment_info?.payment_type || 'N/A',
            paymentGateway: state?.payment_info?.payment_gateway || 'N/A'
        };
    };

    const handleDownloadSellerInvoice = async (sellerIndex: number) => {
        const seller = state?.sellers?.[sellerIndex];
        if (!seller?.seller_invoice?.invoice_url) {
            toast.error('Seller invoice URL not available');
            return;
        }

        try {
            setLoadingSellerInvoice(true);
            // Extract the file key from the full URL
            const invoiceUrl = seller.seller_invoice.invoice_url;
            let fileKey = invoiceUrl;

            // If it's a full URL, extract just the key part
            if (invoiceUrl.includes('amazonaws.com')) {
                const urlParts = invoiceUrl.split('.amazonaws.com/');
                if (urlParts.length > 1) {
                    fileKey = urlParts[1];
                }
            }

            console.log('Original seller invoice URL:', invoiceUrl);
            console.log('Extracted file key:', fileKey);

            // Generate signed URL
            const signedUrl = await getInvoiceSignedUrl(fileKey, 3600);
            console.log('Generated seller invoice signed URL:', signedUrl);

            // Open in new tab
            window.open(signedUrl, '_blank');
        } catch (error) {
            console.error('Error generating seller invoice signed URL:', error);
            toast.error('Failed to generate seller invoice download link');
        } finally {
            setLoadingSellerInvoice(false);
        }
    };

    useEffect(() => {
        initialise();
    }, [match?.params.id, authState?.lang]);

    // Calculate totals
    const totalProducts = state?.sellers?.reduce((acc, seller) => acc + (seller.products?.length || 0), 0) || 0;
    const totalSellers = state?.sellers?.length || 0;

    return (
        <>
            <section className="breadcrum-box">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12">
                            <h2 className="fw-semibold">Order Detail</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/orders/1">Order List</Link></li>
                                    <li className="breadcrumb-item active fw-bold">Order Detail</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <div className='page-spacing'>
                <section className='product-detail'>
                    <div className="container-fluid">
                        {/* Tabs Navigation */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="common-card">
                                    <ul className="nav nav-tabs" role="tablist">
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className={`nav-link ${activeTab === 'order' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('order')}
                                                type="button"
                                                role="tab"
                                            >
                                                <i className="fa fa-shopping-cart me-2"></i>
                                                Order Overview
                                                <span className="badge ms-2 bg-theme">{totalProducts} Products</span>
                                            </button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className={`nav-link ${activeTab === 'seller' ? 'active text-theme' : 'text-dark'}`}
                                                onClick={() => setActiveTab('seller')}
                                                type="button"
                                                role="tab"
                                            >
                                                <i className="fa fa-store me-2"></i>
                                                Sellers
                                                <span className="badge ms-2 btn-theme">{totalSellers} Sellers</span>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Order Tab Content */}
                        {activeTab === 'order' && (
                            <Fragment>
                                {/* Order Summary Cards */}
                                <div className="row gy-3 mb-3">
                                    {/* User Info Card */}
                                    <div className="col-md-6 col-lg-3">
                                        <div className="common-card h-100">
                                            <div className="common-card-title">
                                                <h5><i className="fa fa-user me-2 text-theme"></i>Customer</h5>
                                            </div>
                                            <div className="common-card-content">
                                                <div className="text-center">
                                                    <SignedImage
                                                        src={state?.user_info?.profile_pic
                                                            ? String(state.user_info.profile_pic)
                                                            : ""}
                                                        fallback={profile_placeholder}
                                                        alt={state?.user_info?.name}
                                                        className="rounded-circle mb-2"
                                                        size="small"
                                                        width="80"
                                                        height="80"
                                                    />
                                                    <h6 className="fw-bold mb-1">{state?.user_info?.name || 'N/A'}</h6>
                                                    <p className="text-muted small mb-0">
                                                        <i className="fa fa-phone me-1"></i>
                                                        {state?.address?.phone_no
                                                            ? `+${state.address.country_code}-${state.address.phone_no}`
                                                            : state?.user_info?.phone_no
                                                                ? `+${state.user_info.country_code}-${state.user_info.phone_no}`
                                                                : 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Products Count Card */}
                                    <div className="col-md-6 col-lg-3">
                                        <div className="common-card h-100">
                                            <div className="common-card-title">
                                                <h5><i className="fa fa-box me-2 text-theme"></i>Products</h5>
                                            </div>
                                            <div className="common-card-content">
                                                <div className="text-center">
                                                    <h2 className="fw-bold mb-0 text-theme">{totalProducts}</h2>
                                                    <p className="text-muted small mb-0">Total Items Ordered</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sellers Count Card */}
                                    <div className="col-md-6 col-lg-3">
                                        <div className="common-card h-100">
                                            <div className="common-card-title">
                                                <h5><i className="fa fa-store me-2 text-theme" ></i>Sellers</h5>
                                            </div>
                                            <div className="common-card-content">
                                                <div className="text-center">
                                                    <h2 className="fw-bold mb-0 text-theme">{totalSellers}</h2>
                                                    <p className="text-muted small mb-0">Different Sellers</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order Status & Type Card */}
                                    <div className="col-md-6 col-lg-3">
                                        <div className="common-card h-100">
                                            <div className="common-card-title">
                                                <h5><i className="fa fa-info-circle me-2 text-theme"></i>Order Info</h5>
                                            </div>
                                            <div className="common-card-content">
                                                <div className="text-center">
                                                    {state?.sellers?.[0]?.products?.[0] && (
                                                        <OrderStatus {...state.sellers[0].products[0]} />
                                                    )}
                                                    {/* <p className="text-muted small mt-2 mb-0">
                                                        <i className="fa fa-truck me-1"></i>
                                                        <strong>{state?.delivery_type || 'N/A'}</strong>
                                                    </p> */}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Customer Invoice Section */}
                                <div className="row gy-3 mb-3">
                                    <div className="col-12">
                                        <div className="common-card">
                                            <div className="common-card-title d-flex justify-content-between align-items-center">
                                                <h5><i className="fa fa-file-invoice me-2"></i>Customer Invoice</h5>
                                                {state?.customer_invoice?.invoice_url && (
                                                    <button
                                                        onClick={handleDownloadInvoice}
                                                        className="btn btn-sm btn-theme"
                                                        disabled={loadingInvoice}
                                                        style={{ backgroundColor: '#1AA7DE', borderColor: '#1AA7DE' }}
                                                    >
                                                        {loadingInvoice ? (
                                                            <>
                                                                <i className="fa fa-spinner fa-spin me-1"></i>Generating...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fa fa-download me-1"></i>Download Invoice
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="common-card-content">
                                                <div className="table-responsive">
                                                    <table className="table table-bordered">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Order ID</th>
                                                                <th>Subtotal</th>
                                                                <th>Delivery Charge</th>
                                                                {Number(state?.customer_invoice?.coupon_discount) > 0 && <th>Coupon Discount</th>}
                                                                <th>Tax Amount</th>
                                                                {Number(state?.customer_invoice?.platform_fee) > 0 && <th>Platform Fee</th>}
                                                                <th>Total Price</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td><strong>{state?.customer_invoice?.order_id || 'N/A'}</strong></td>
                                                                <td>{CURRENCY_SYMBOL}{numberWithCommas(state?.customer_invoice?.subtotal || 0)}</td>
                                                                <td>{CURRENCY_SYMBOL}{numberWithCommas(state?.customer_invoice?.delivery_charge || 0)}</td>
                                                                {Number(state?.customer_invoice?.coupon_discount) > 0 && <td>{CURRENCY_SYMBOL}{numberWithCommas(state?.customer_invoice?.coupon_discount || 0)}</td>}
                                                                <td>{CURRENCY_SYMBOL}{numberWithCommas(state?.customer_invoice?.tax_amount || 0)}</td>
                                                                {Number(state?.customer_invoice?.platform_fee) > 0 && <td>{CURRENCY_SYMBOL}{numberWithCommas(state?.customer_invoice?.platform_fee || 0)}</td>}
                                                                <td className="fw-bold text-theme">{CURRENCY_SYMBOL}{numberWithCommas(state?.customer_invoice?.total_price || 0)}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row gy-3 mb-3">
                                    {/* Delivery Address - Show only if data exists */}
                                    {state?.address && state?.address?.full_address && (
                                        <div className="col-md-6">
                                            <div className="common-card h-100">
                                                <div className="common-card-title">
                                                    <h5><i className="fa fa-map-marker-alt me-2" style={{ color: '#ed5565' }}></i>Delivery Address</h5>
                                                </div>
                                                <div className="common-card-content">
                                                    <address className="mb-0">
                                                        <strong>{state?.address?.name || 'N/A'}</strong><br />
                                                        {state?.address?.full_address || 'N/A'}<br />
                                                        {state?.address?.city}, {state?.address?.state} - {state?.address?.pin_code}<br />
                                                        {state?.address?.country}<br />
                                                        <i className="fa fa-phone me-1"></i> {state?.address?.phone_no || 'N/A'}<br />
                                                        <span className="badge" style={{ backgroundColor: '#1AA7DE' }}>{state?.address?.address_type || 'N/A'}</span>
                                                    </address>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Payment Info Section */}
                                    {state?.payment_info && (
                                        <div className="row gy-3 mb-3">
                                            <div className="col-12">
                                                <div className="common-card">
                                                    <div className="common-card-title">
                                                        <h5><i className="fa fa-credit-card me-2"></i>Payment Info</h5>
                                                    </div>
                                                    <div className="common-card-content">
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered mb-0">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Payment By</th>
                                                                        <th>Payment Gateway</th>
                                                                        <th>Transaction ID</th>
                                                                        <th>Amount Paid</th>
                                                                        <th>Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr>
                                                                        <td><strong>{getPaymentDisplay().paymentBy}</strong></td>
                                                                        <td>{getPaymentDisplay().paymentGateway}</td>
                                                                        <td><small>{state.payment_info.transaction_id || 'N/A'}</small></td>
                                                                        <td>{state.payment_info.currency.toUpperCase() + " " + numberWithCommas(state.payment_info.total_amount || 0)}</td>
                                                                        <td>
                                                                            <span
                                                                                className={`badge ${state.payment_info.payment_status === 'complete' ? 'bg-success' :
                                                                                    state.payment_info.payment_status === 'failed' ? 'bg-danger' : 'bg-warning'
                                                                                    }`}
                                                                                style={{
                                                                                    backgroundColor:
                                                                                        state.payment_info.payment_status === 'complete' ? '#23c6c8' :
                                                                                            state.payment_info.payment_status === 'failed' ? '#ed5565' : '#f8ac59'
                                                                                }}
                                                                            >
                                                                                {state.payment_info.payment_status?.toUpperCase()}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Order Timeline */}
                                    <div className={state?.address && state?.address?.full_address ? "col-md-6" : "col-md-12"}>
                                        <div className="common-card h-100">
                                            <div className="common-card-title">
                                                <h5><i className="fa fa-clock me-2 text-theme"></i>Order Timeline</h5>
                                            </div>
                                            <div className="common-card-content">
                                                <ul className="list-unstyled mb-0">
                                                    <li className="mb-2">
                                                        <i className="fa fa-calendar-check me-2 text-theme"></i>
                                                        <strong>Ordered:</strong> {state?.created_at ? moment(state.created_at).format('DD MMM YYYY, hh:mm A') : 'N/A'}
                                                    </li>
                                                    <li className="mb-2">
                                                        <i className="fa fa-calendar-update me-2 text-theme" ></i>
                                                        <strong>Updated:</strong> {state?.updated_at ? moment(state.updated_at).format('DD MMM YYYY, hh:mm A') : 'N/A'}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    
                                </div>
                            </Fragment>
                        )}

                        {/* Seller Tab Content */}
                        {activeTab === 'seller' && (
                            <Fragment>
                                {/* Seller Selection Tabs (if multiple sellers) */}
                                {totalSellers > 1 && (
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="common-card">
                                                <div className="common-card-title">
                                                    <h5>Select Seller</h5>
                                                </div>
                                                <div className="common-card-content">
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {state?.sellers?.map((seller, index) => (
                                                            <button
                                                                key={seller.seller_id}
                                                                className={`btn ${selectedSellerIndex === index ? 'btn-theme' : 'btn-outline-theme'}`}
                                                                onClick={() => setSelectedSellerIndex(index)}
                                                                style={selectedSellerIndex === index ? { backgroundColor: '#1AA7DE', borderColor: '#1AA7DE' } : {}}
                                                            >
                                                                <i className="fa fa-store me-1"></i>
                                                                {seller.seller_name} ({seller.products?.length || 0} products)
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Selected Seller Details */}
                                {state?.sellers && state.sellers[selectedSellerIndex] && (
                                    <Fragment>
                                        {/* Seller Info & Financial Summary */}
                                        <div className="row gy-3 mb-3">
                                            <div className="col-md-4">
                                                <div className="common-card h-100">
                                                    <div className="common-card-title">
                                                        <h5><i className="fa fa-user-tie me-2"></i>Seller Details</h5>
                                                    </div>
                                                    <div className="common-card-content">
                                                        <div className="text-center mb-3">
                                                            <SignedImage
                                                                src={state.sellers[selectedSellerIndex].seller_image
                                                                    ? String(state.sellers[selectedSellerIndex].seller_image)
                                                                    : ""}
                                                                fallback={profile_placeholder}
                                                                alt={state.sellers[selectedSellerIndex].seller_name}
                                                                className="rounded-circle mb-2"
                                                                size="small"

                                                                width="100"
                                                                height="100"
                                                            />
                                                            <h5 className="fw-bold mb-1">{state.sellers[selectedSellerIndex].seller_name}</h5>
                                                            <p className="text-muted small mb-0">{state.sellers[selectedSellerIndex].seller_company || 'N/A'}</p>
                                                        </div>
                                                        <hr />
                                                        <ul className="list-unstyled mb-0">
                                                            <li className="mb-2">
                                                                <i className="fa fa-phone me-2 text-theme"></i>
                                                                {state.sellers[selectedSellerIndex].seller_phone || 'N/A'}
                                                            </li>
                                                            <li className="mb-2">
                                                                <i className="fa fa-map-marker-alt me-2 text-danger"></i>
                                                                {state.sellers[selectedSellerIndex].seller_address || 'N/A'}
                                                            </li>
                                                            <li>
                                                                <Link to={`/seller/${state.sellers[selectedSellerIndex].seller_id}`} className="btn btn-sm btn-theme w-100" style={{ backgroundColor: '#1AA7DE', borderColor: '#1AA7DE' }}>
                                                                    <i className="fa fa-eye me-1"></i>View Profile
                                                                </Link>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Seller Price Breakdown */}
                                            <div className="col-md-8">
                                                <div className="common-card h-100">
                                                    <div className="common-card-title d-flex justify-content-between align-items-center">
                                                        <h5><i className="fa fa-calculator me-2"></i>Price Breakdown</h5>
                                                        {(state?.sellers[selectedSellerIndex]?.products?.[0]?.order_status === henceofrthEnums.OrderStatus.CONFIRMED ||
                                                            state?.sellers[selectedSellerIndex]?.products?.[0]?.order_status === henceofrthEnums.OrderStatus.DELIVERED) && (
                                                                <button
                                                                    onClick={() => handleDownloadSellerInvoice(selectedSellerIndex)}
                                                                    className="btn btn-sm btn-theme"
                                                                    disabled={loadingSellerInvoice}
                                                                    style={{ backgroundColor: '#1AA7DE', borderColor: '#1AA7DE' }}
                                                                >
                                                                    {loadingSellerInvoice ? (
                                                                        <>
                                                                            <i className="fa fa-spinner fa-spin me-1"></i>Generating...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="fa fa-download me-1"></i>Download Invoice
                                                                        </>
                                                                    )}
                                                                </button>
                                                            )}
                                                    </div>
                                                    <div className="common-card-content">
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered mb-0">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Description</th>
                                                                        <th>Amount ({CURRENCY_SYMBOL})</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr>
                                                                        <td>Total Product Price</td>
                                                                        <td>{CURRENCY_SYMBOL}{numberWithCommas(state.sellers[selectedSellerIndex].total_price || 0)}</td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td>Delivery Charge</td>
                                                                        <td>{CURRENCY_SYMBOL}{numberWithCommas(state.sellers[selectedSellerIndex].delivery_price || 0)}</td>
                                                                    </tr>
                                                                    {Number(state.sellers[selectedSellerIndex].coupon_discount) > 0 &&
                                                                        <tr>
                                                                            <td>Coupon Discount</td>
                                                                            <td className="text-danger">- {CURRENCY_SYMBOL}{numberWithCommas(state.sellers[selectedSellerIndex].coupon_discount || 0)}</td>
                                                                        </tr>}
                                                                    <tr>
                                                                        <td>Tax Amount</td>
                                                                        <td>{CURRENCY_SYMBOL}{numberWithCommas(state.sellers[selectedSellerIndex].tax_amount || 0)}</td>
                                                                    </tr>
                                                                    {Number(state.sellers[selectedSellerIndex].seller_platform_fee) > 0 &&
                                                                        <tr>
                                                                            <td>Platform Fee</td>
                                                                            <td>{CURRENCY_SYMBOL}{numberWithCommas(state.sellers[selectedSellerIndex].seller_platform_fee || 0)}</td>
                                                                        </tr>}
                                                                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                                                                        <td><strong>Admin Commission</strong></td>
                                                                        <td><strong style={{ color: '#ed5565' }}>{CURRENCY_SYMBOL}{numberWithCommas(state.sellers[selectedSellerIndex].admin_commission || 0)}</strong></td>
                                                                    </tr>
                                                                    <tr style={{ backgroundColor: '#e8f8f5' }}>
                                                                        <td><strong>Seller Earnings</strong></td>
                                                                        <td><strong className='text-theme'>{CURRENCY_SYMBOL}{numberWithCommas(state.sellers[selectedSellerIndex].seller_earnings || 0)}</strong></td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Seller Products Table */}
                                        <div className="row gy-3 mb-3">
                                            <div className="col-12">
                                                <div className="common-card">
                                                    <div className="common-card-title">
                                                        <h5><i className="fa fa-box me-2"></i>Products from {state.sellers[selectedSellerIndex].seller_name}</h5>
                                                    </div>
                                                    <div className="common-card-content">
                                                        <div className="table-responsive">
                                                            <table className="table table-striped align-middle">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>#</th>
                                                                        <th>Image</th>
                                                                        <th>Product Name</th>
                                                                        {/* <th>Product ID</th> */}
                                                                        <th>Qty</th>
                                                                        <th>Price</th>
                                                                        <th>Discount</th>
                                                                        <th>Final Price</th>
                                                                        <th>Status</th>
                                                                        <th>Delivery Date</th>
                                                                        <th>Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {state.sellers[selectedSellerIndex].products?.map((product, index) => (
                                                                        <tr key={product.order_product_id}>
                                                                            <td>{index + 1}</td>
                                                                            <td>
                                                                                {product.product_images?.[0] ? (
                                                                                    <SignedImage
                                                                                        src={product.product_images[0]}
                                                                                        fallback={profile_placeholder}
                                                                                        alt={product.product_name}
                                                                                        className="w-25"
                                                                                        width="50"
                                                                                        height="50"
                                                                                        size="small"
                                                                                    />
                                                                                ) : (
                                                                                    <img
                                                                                        src={profile_placeholder}
                                                                                        alt="placeholder"
                                                                                        className="rounded"
                                                                                        width="50"
                                                                                        height="50"
                                                                                    />
                                                                                )}
                                                                            </td>
                                                                            <td>
                                                                                <Link to={`/product/${product.product_id}`}>
                                                                                    <strong>{product.product_name}</strong>
                                                                                </Link>
                                                                            </td>
                                                                            {/* <td><small>{product.product_id}</small></td> */}
                                                                            <td>{product.quantity}</td>
                                                                            <td>{CURRENCY_SYMBOL}{numberWithCommas(product.price)}</td>
                                                                            <td>
                                                                                <span className="badge" style={{ backgroundColor: '#f8ac59', color: '#fff' }}>
                                                                                    {product.discount_percentage}% OFF
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <strong>{CURRENCY_SYMBOL}{numberWithCommas(product.discount_price)}</strong>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`badge ${product.order_status === 'DELIVERED' ? 'bg-success' :
                                                                                    product.order_status === 'CANCELLED' ? 'bg-danger' :
                                                                                        'bg-warning'
                                                                                    }`} style={{
                                                                                        backgroundColor: product.order_status === 'DELIVERED' ? '#23c6c8' :
                                                                                            product.order_status === 'CANCELLED' ? '#ed5565' :
                                                                                                '#f8ac59'
                                                                                    }}>
                                                                                    {product.order_status}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                {product.delivery_date
                                                                                    ? moment(Number(product.delivery_date)).format('DD MMM YYYY')
                                                                                    : 'N/A'}
                                                                            </td>
                                                                            <td>
                                                                                <Link to={`/product/${product.product_id}`} className="btn btn-sm btn-theme" style={{ backgroundColor: '#1AA7DE', borderColor: '#1AA7DE' }}>
                                                                                    <i className="fa fa-eye me-1"></i>View
                                                                                </Link>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Fragment>
                                )}
                            </Fragment>
                        )}
                    </div>
                </section>
            </div>
        </>
    );
};

export default OrderDetailsNew;
